import React, { createContext, useState, useEffect, useContext } from 'react';
import { 
  auth, 
  registerUser, 
  loginUser, 
  logoutUser, 
  resetPassword as firebaseResetPassword
} from '../config/firebase';
import api from '../services/api';
import { onAuthStateChanged, updateProfile } from 'firebase/auth';

// Create context
export const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Listen for Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setLoading(true);
      try {
        if (firebaseUser) {
          // User is signed in
          await fetchUserProfile(firebaseUser);
        } else {
          // User is signed out
          setUser(null);
        }
      } catch (err) {
        console.error('Auth state change error:', err);
        setError('Authentication error. Please try again.');
      } finally {
        setLoading(false);
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  // Fetch additional user profile data from backend
  const fetchUserProfile = async (firebaseUser) => {
    try {
      // We already have basic user info from Firebase
      const userProfile = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName || 'User',
        photoURL: firebaseUser.photoURL,
        emailVerified: firebaseUser.emailVerified
      };
      
      // Fetch additional user data from backend
      try {
        // Get a fresh token before making the API call
        const token = await firebaseUser.getIdToken(true);
        
        // Set the token in the authorization header
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Make the API call to get the user profile
        const response = await api.get('/auth/me');
        
        if (response.data.status === 'success') {
          // Merge Firebase user with additional profile data
          setUser({
            ...userProfile,
            ...response.data.data
          });
        } else {
          setUser(userProfile);
        }
      } catch (err) {
        console.error('Error fetching user profile:', err);
        // Still set the basic user profile even if backend request fails
        setUser(userProfile);
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
      throw err;
    }
  };

  // Register user
  const register = async ({ email, password, name }) => {
    try {
      setLoading(true);
      const { user: firebaseUser, error } = await registerUser(email, password);
      
      if (error) {
        throw error;
      }
      
      // Update display name if provided
      if (name && firebaseUser) {
        await firebaseUser.updateProfile({
          displayName: name
        });
      }
      
      // User is automatically signed in after registration
      return { success: true, user: firebaseUser };
    } catch (err) {
      let errorMessage = 'Registration failed';
      
      if (err.code === 'auth/email-already-in-use') {
        errorMessage = 'Email is already in use';
      } else if (err.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      }
      
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Login user
  const login = async ({ email, password }) => {
    try {
      setLoading(true);
      const { user: firebaseUser, error } = await loginUser(email, password);
      
      if (error) {
        throw error;
      }
      
      return { success: true, user: firebaseUser };
    } catch (err) {
      let errorMessage = 'Login failed';
      
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        errorMessage = 'Invalid email or password';
      } else if (err.code === 'auth/too-many-requests') {
        errorMessage = 'Too many unsuccessful login attempts. Please try again later.';
      }
      
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Logout user
  const logout = async () => {
    try {
      setLoading(true);
      await logoutUser();
      return { success: true };
    } catch (err) {
      console.error('Logout error:', err);
      setError('Logout failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Forgot password
  const forgotPassword = async (email) => {
    try {
      setLoading(true);
      await firebaseResetPassword(email);
      return { success: true, message: 'Password reset email sent' };
    } catch (err) {
      let errorMessage = 'Password reset request failed';
      
      if (err.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      }
      
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update user profile
  const updateUserProfile = async (profileData) => {
    try {
      setLoading(true);
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }
      
      // Update profile in Firebase using the imported updateProfile function
      await updateProfile(currentUser, {
        displayName: profileData.displayName || currentUser.displayName
      });
      
      // Update local state
      setUser(prevUser => ({
        ...prevUser,
        displayName: profileData.displayName || prevUser.displayName
      }));
      
      // Update profile in backend (if needed)
      try {
        const token = await currentUser.getIdToken(true);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        await api.put('/auth/me', {
          displayName: profileData.displayName
        });
      } catch (err) {
        console.error('Error updating profile in backend:', err);
        // We don't throw here because Firebase update succeeded
      }
      
      return { success: true, message: 'Profile updated successfully' };
    } catch (err) {
      console.error('Error updating user profile:', err);
      setError('Failed to update profile');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        register,
        login,
        logout,
        forgotPassword,
        updateUserProfile,
        setError
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
